import { getPrimaryColor } from '#lib/helper/color.js';
import { handleApiError } from '#lib/stores/api.js';
import { writable } from 'svelte/store';

export enum FormStatus {
	Idle = 'idle',
	Processing = 'processing',
	Success = 'success',
	Error = 'error'
}

export const formStatus = writable<FormStatus>(FormStatus.Idle);
export const currentItemId = writable<string | number | undefined>(undefined);

formStatus.subscribe((value) => {
	if (value == FormStatus.Success) {
		setTimeout(() => {
			formStatus.set(FormStatus.Idle);
			currentItemId.set(undefined);
		}, 1000);
	} else if (value == FormStatus.Error) {
		setTimeout(() => {
			formStatus.set(FormStatus.Idle);
			setTimeout(() => {
				currentItemId.set(undefined);
			}, 400);
		}, 1000);
	}
	return () => {};
});

type FormStatusColorOptions = {
	inHex?: boolean;
	defaultColor?: string;
	opacity?: number;
};

export function formStateColor(state: FormStatus, options: FormStatusColorOptions = {}) {
	const { inHex = false, defaultColor, opacity = 0 } = options;

	if (!inHex) {
		switch (state) {
			case FormStatus.Processing:
				return `warning${opacity ? '/' + opacity * 100 : ''}`;
			case FormStatus.Success:
				return `success${opacity ? '/' + opacity * 100 : ''}`;
			case FormStatus.Error:
				return `error${opacity ? '/' + opacity * 100 : ''}`;
			default:
				return defaultColor ?? 'primary';
		}
	} else {
		// convert opacity 0-1 to 00-FF
		const opacityString = opacity ? Math.round(opacity * 255).toString(16) : '';
		switch (state) {
			case FormStatus.Processing:
				return `#ff9500${opacityString}`;
			case FormStatus.Success:
				return `#0A9668${opacityString}`;
			case FormStatus.Error:
				return `#f56565${opacityString}`;
			default:
				return defaultColor ?? getPrimaryColor();
		}
	}
}

let debounce: ReturnType<typeof setTimeout> | undefined;

export function saveForm<T>(
	saveAction: (payload: unknown) => Promise<unknown>,
	form: T,
	isValid: () => boolean,
	hasChanges: (form: T) => boolean,
	preSave: (form: T) => void,
	onSaved: (res: unknown) => void,
	onError: (err: string) => void,
	transform: (form: T) => unknown = (form) => form
) {
	if (form && hasChanges(form) && isValid()) {
		formStatus.set(FormStatus.Processing);

		if (debounce) clearTimeout(debounce);

		debounce = setTimeout(() => {
			if (!isValid()) {
				formStatus.set(FormStatus.Error);
				return;
			}
			if (!hasChanges(form)) {
				formStatus.set(FormStatus.Success);
				return;
			}
			preSave(form);

			saveAction(transform(form))
				.then((res) => {
					onSaved(res);
					formStatus.set(FormStatus.Success);
				})
				.catch((err: unknown) => {
					formStatus.set(FormStatus.Error);
					handleApiError(err, (apiErr) => {
						onError(apiErr.error);
					});
				});
		}, 1000);
	} else if (form && hasChanges(form) && !isValid()) {
		formStatus.set(FormStatus.Error);
	}
}

let debouncherTimeout: NodeJS.Timeout | undefined = undefined;
export function debouncer(fn: () => void, timeout: number = 400) {
	clearTimeout(debouncherTimeout);
	debouncherTimeout = setTimeout(() => {
		fn();
	}, timeout);
}
